import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import lookup from '../../../../assets/certificates/Tables/lookup.svg'
import editPen from '../../../../assets/certificates/Tables/pencil.svg'
import { GlobalTable, HeaderLine, Td, TdButtons, Th, Tr, FooterTr } from '../../../../components/styles/table-style'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { CertificateOutletType } from '..'
import axios from '../../../../config/axios'
import UserContext from '../../../../contexts/UserContext'
import trash from '../../../../assets/router/trash.svg'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../../components/ModalConfirmation'

interface CertificateTypeForTable {
  id: number,
  param: string
  supplier: string
  customer: string
  heatNumber: string
  type: {id:number, name:string}
  supplierIsMain: boolean
  date: string
  codeOpString: string
}

const TableRoot = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
`

const CertificatesTable: React.FC = () => {
  const [ certificates, setCertificates ] = useState<CertificateTypeForTable[]>([])
  const [ visibleCount, setVisibleCount ] = useState<number>(50)
  const [ totalCount, setTotalCount ] = useState<number | null>(null)
  const { setTabIndex, appliedFilters, search } = useOutletContext<CertificateOutletType>()
  const navigate = useNavigate()
  const { user } = useContext(UserContext)
  const { setFeedback } = useContext(FeedbackContext)
  
  const [deleteModal, setDeleteModal] = useState<{isOpen: boolean, id: number | null}>({ isOpen: false, id: null })
  
  const handleConfirmDelete = async () => {
    if (deleteModal.id !== null) {
      await deleteCertificate(deleteModal.id)
      setDeleteModal({ isOpen: false, id: null })
    }
  }

  const deleteCertificate = async (id: number) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/certificate/${id}`,
      )
      setCertificates((prev) =>
        prev.filter((cert) => cert.id !== id),
      )
      if (totalCount !== null) {
        setTotalCount((prev) => (prev ? prev - 1 : prev))
      }
      setFeedback({
        isOpen: true,
        message: 'Certificado deletado com sucesso!',
        statusOk: true,
      })
    } catch {
      setFeedback({
        isOpen: true,
        message: 'Erro ao deletar certificado',
        statusOk: false,
      })
    }
  }
  
  useEffect(() => {
    setTabIndex(0)
  }, [])
  
  useEffect(() => {
    const params: Record<string, string | number> = {
      limit: visibleCount,
      offset: 0,
    }
    if (search?.trim()) {
      params.search = search.trim()
    }
    if (appliedFilters.length > 0) {
      params.filters = JSON.stringify(appliedFilters)
    }

    axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/certificate`, { params })
      .then(response => {

        if (response.data) {
          if (Array.isArray(response.data)) {
            setCertificates(response.data)
            setTotalCount(response.data.length)
          } else if (response.data.data) {
            setCertificates(response.data.data)
            setTotalCount(response.data.total ?? response.data.data.length)
          }
        }
      })
      .catch(error => {
        console.error(error)
      })
  }, [appliedFilters, search, visibleCount])
  
  const columns = ['Parâmetro', 'Fornecedor', 'Cliente', 'Tipo do certificado', 'Nº da corrida', 'Data de criação', 'OP']

  return (
    <TableRoot>
      <GlobalTable>
        <thead>
          <HeaderLine>
            {columns.map((item, colIndex) => (
              <Th key={colIndex}>{item}</Th>
            ))}
            <Th></Th>
          </HeaderLine>
        </thead>
        <tbody>
          {certificates.map((certificate, index) => 
            <Tr key={index}>
              <Td>{certificate.param}</Td>
              <Td>{certificate.supplier}</Td>
              <Td>{certificate.customer}</Td>
              <Td>{certificate.type.id === 1 && certificate.supplierIsMain ? 'Qualidade': certificate.type.id === 1 ? 'Origem' : certificate.type.name}</Td>
              <Td>{certificate.heatNumber}</Td>
              <Td>{certificate.date}</Td>
              <Td>{certificate.codeOpString}</Td>
              <TdButtons>
                <img src={lookup} alt='looup-icon' onClick={() => navigate(`view/${certificate.id}`)} />
                <img src={editPen} alt='edit-icon' onClick={() => navigate(`edit?id=${certificate.id}`)} />
                <img src={trash} alt='trash-icon' onClick={() => setDeleteModal({ isOpen: true, id: certificate.id })} />
              </TdButtons>
            </Tr>
          )}
        </tbody>
        <tfoot>
          <FooterTr>
            <Td colSpan={columns.length + 1} style={{ textAlign: 'center', background: 'transparent' }}>
              <LoadMoreButton
                currentCount={certificates.length}
                totalCount={totalCount ?? certificates.length}
                onLoadMore={() => setVisibleCount(prev => prev + 50)}
                hasMore={typeof totalCount === 'number' ? certificates.length < totalCount : false}
              />
            </Td>
          </FooterTr>
        </tfoot>
      </GlobalTable>
      
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        title="Tem certeza que deseja excluir?"
        message="Esta ação não poderá ser desfeita. O certificado será removido e os estoques relacionados serão desvinculados."
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setDeleteModal({ isOpen: false, id: null }),
            variant: 'outline'
          },
          {
            label: 'Excluir',
            onClick: handleConfirmDelete,
            variant: 'danger'
          }
        ]}
      />
    </TableRoot>
  )
}
export default CertificatesTable